import React, { useContext, useCallback, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { convertUndefinedFields } from 'utils/helpers';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import {
	IEditModGroupRestaurantPropertiesContext,
	EditModGroupRestaurantPropertiesContext,
} from '../../editModGroupRestaurantPropertiesState/editModGroupRestaurantPropertiesContext';
import { updateModGroupRestaurantProperties } from 'backend/modifierGroups';
import { useHistory } from 'react-router-dom';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { SaveAsPatchModal } from './SaveAsPatchModal/index';
import { ModalEditInclusiveForModGroup } from 'components/ModalWindows/ModalEditInclusiveForModGroup';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		showErrorModalWithInfo,
		refetchRestPropsData,
		dataForSubmit,
		typeOfModalWindow,
		errorResponseData,
		modGroupId,
		modGroupName,
		customerName,
		IDSelectedRestaurant,
		fetchedRestaurants,
		parents,
	} = useContext<IEditModGroupRestaurantPropertiesContext>(EditModGroupRestaurantPropertiesContext);
	const history = useHistory();

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModGroupRestaurantProperties(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.MENU_CIRCULAR_REFERENCE_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
					changeTypeOfModalWindow('errorWithInfo');
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit, showLoader, hideLoader, changeTypeOfModalWindow, showErrorModalWithInfo]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, [changeTypeOfModalWindow]);

	const handleCloseModalWithRefetchRestPropsData = useCallback(() => {
		refetchRestPropsData(true);
		changeTypeOfModalWindow(null);
	}, [refetchRestPropsData, changeTypeOfModalWindow]);

	const handleCloseModalWithError = useCallback(() => {
		changeTypeOfModalWindow('error');
	}, [changeTypeOfModalWindow]);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, [showErrorModalWithInfo, changeTypeOfModalWindow]);

	const selectedRestaurant = useMemo(() => {
		return fetchedRestaurants.find((r) => r.id === IDSelectedRestaurant) || null;
	}, [fetchedRestaurants, IDSelectedRestaurant]);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!errorResponseData}>
			{typeOfModalWindow === 'errorWithInfo' && errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description="MenuCircularReference"
					translationOptions={{ name: errorResponseData.Metadata.modifierName }}
				/>
			)}
			{typeOfModalWindow === 'toConfirmFieldsClearing' && (
				<ModalToConfirm
					onConsentEvent={() => history.push(`/menu-v2/modifier-groups/view?modifierGroupID=${modGroupId}`)}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="cancelSaveInfo"
				/>
			)}
			{typeOfModalWindow === 'toConfirmSubmit' && (
				<ModalToConfirm
					onConsentEvent={handleConfirm}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="saveInfo"
				/>
			)}
			{typeOfModalWindow === 'success' && (
				<ModalSentSuccessfully
					onConsentEvent={handleCloseModalWithRefetchRestPropsData}
					isVisible={true}
					description="savedSuccessfully"
				/>
			)}
			{typeOfModalWindow === 'error' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorWhileSaving" />
			)}
			{typeOfModalWindow === 'saveAsPatch' && (
				<SaveAsPatchModal onCloseModal={handleCloseModalWithRefetchRestPropsData} />
			)}
			{typeOfModalWindow === 'editInclusive' && IDSelectedRestaurant && (
				<ModalEditInclusiveForModGroup
					onCloseModal={handleCloseModalWithRefetchRestPropsData}
					onFailureEvent={handleCloseModal}
					onShowError={handleCloseModalWithError}
					restaurantId={IDSelectedRestaurant}
					modifierGroupId={modGroupId}
					modifierGroupName={modGroupName}
					customerName={customerName}
					restaurantName={selectedRestaurant?.name || ''}
					parents={parents}
				/>
			)}
		</ModalWrapper>
	);
};
